import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Paper,
} from '@mui/material';
import {
  addDoc,
  collection,
  doc,
  setDoc,
  updateDoc,
  increment,
  serverTimestamp,
  getDoc,
} from "firebase/firestore";
import { db, auth } from "../Firebase";
import CallIcon from '@mui/icons-material/Call';
import EmailIcon from '@mui/icons-material/Email';
import ChatIcon from '@mui/icons-material/Chat';
import { useTranslation } from 'react-i18next';
import ContactImg from '../Img/contact.png'; 
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import IconButton from '@mui/material/IconButton';


function Contact() {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [infoBox, setInfoBox] = useState(null);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // Submit message
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.message.trim()) return;

    setSubmitting(true);

    try {
      // 1) Save message in Firestore so Admin can read it from `contactUs`
      const user = auth?.currentUser;

      let senderFullName = "—";
      let senderPhone = "—";
      let senderEmail = form.email || "—";

      if (user?.uid) {
        // Auth email (usually exists)
        senderEmail = user.email || senderEmail;

        // Get profile data from Firestore users/{uid}
        const userSnap = await getDoc(doc(db, "users", user.uid));
        if (userSnap.exists()) {
          const u = userSnap.data();
          senderFullName = u.fullName || user.displayName || "—";
          senderPhone = u.phone || "—";
        } else {
          // fallback if the user doc doesn't exist
          senderFullName = user.displayName || "—";
        }
      }

      await addDoc(collection(db, "contactUs"), {
        fullName: senderFullName,
        email: senderEmail,
        phone: senderPhone,
        message: form.message.trim(),
        date: new Date().toISOString(),
        createdAt: serverTimestamp(),
        status: "unread",
      });

      // 2) Increment Admin notification counter
      const notifRef = doc(db, "AdminNotifications", "main");

      // If doc might not exist yet, create it (merge) then increment
      await setDoc(notifRef, { newMessages: 0 }, { merge: true });
      await updateDoc(notifRef, { newMessages: increment(1) });

      // UI success
      setSubmitted(true);
      setForm({ name: "", email: "", message: "" });
      setInfoBox(null);
    } catch (err) {
      console.error("Failed to submit message:", err);
      setSubmitted(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    
    <Box sx={{maxWidth: 600, mx: 'auto' }}>
      {/* Top Illustration */}
      <Box display="flex" justifyContent="center">
        <Box
          component="img"
          src={ContactImg}
          alt="Team illustration"
          sx={{ width: '100%', maxWidth: 900 }}
        />
      </Box>

      {/* Contact Options */}
      <Box display="flex" justifyContent="space-between" mb={3} mt={3} gap={2}>
        {[
          { icon: <ChatIcon />, label: t('Chat'), type: 'chat' },
          { icon: <EmailIcon />, label: t('Email Us'), type: 'email' },
          { icon: <CallIcon />, label: t('Call Us'), type: 'call' },
        ].map((item, idx) => (
          <Paper
            key={idx}
            elevation={1}
            onClick={() => setInfoBox(item.type)}
            sx={{
              flex: 1,
              height: 90,
              borderRadius: 3,
              backgroundColor: item.color,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              cursor: 'pointer',
              fontWeight: 'bold',
              fontSize: '0.9rem',
              textAlign: 'center',
            }}
          >
            {item.icon}
            <Typography>{item.label}</Typography>
          </Paper>
        ))}
      </Box>

      {infoBox === 'call' && (
        <Box
          sx={{
            backgroundColor: '#fff',
            p: 2,
            borderRadius: 2,
            mb: 2,
            mt: 1,
            boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography fontWeight="bold">📞 +1 234 567 890</Typography>
          <IconButton onClick={() => navigator.clipboard.writeText('+1234567890')}>
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Box>
      )}


      {infoBox === 'email' && (
        <Box
          sx={{
            backgroundColor: '#fff',
            p: 2,
            borderRadius: 2,
            mb: 2,
            mt: 1,
            boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography fontWeight="bold">📧 contact@sofben.ca</Typography>
          <IconButton onClick={() => navigator.clipboard.writeText('support@example.com')}>
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Box>
      )}


      {/* Quick Contact Form */}
      <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
        <Typography
          variant="h6"
          fontWeight="bold"
          textAlign="center"
          sx={{ mb: 2 }}
        >
          {t('Contact our support team:')}
        </Typography>

        <Typography
          variant="body1"
          textAlign="center"
          sx={{ mb: 2 }}
        >
          {t('Type your enquiry messages on the box below and we will reach back to you ver soon')}
        </Typography>

        <form onSubmit={handleSubmit}>
          <TextField
            label={t('Message')}
            name="message"
            multiline
            rows={4}
            fullWidth
            required
            value={form.message}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={submitting}
            sx={{
              backgroundColor: '#c93d3d',
              fontWeight: 'bold',
              borderRadius: 2,
              py: 1.3,
              '&:hover': { backgroundColor: '#a93131' },
            }}
          >
            {submitting ? t("Sending...") : t("Send")}
          </Button>
        </form>

        {submitted && (
          <Alert severity="success" sx={{ mt: 3 }}>
            {t('Message sent successfully!')}
          </Alert>
        )}
      </Paper>
    </Box>
  );
}

export default Contact;
